/**
 * Hook pour obtenir le style de background de l'utilisateur
 *
 * Retourne le style CSS à appliquer au dashboard basé sur le background choisi
 */

import { useMemo } from 'react';
import { useAuthStore } from '@/stores/authStore';

// Backgrounds prédéfinis (doit correspondre au backend)
const PREDEFINED_BACKGROUNDS: Record<string, { value: string; type: string; backgroundSize?: string }> = {
  'gradient-teal': {
    type: 'gradient',
    value: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 50%, #5eead4 100%)',
  },
  'gradient-ocean': {
    type: 'gradient',
    value: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 50%, #22d3ee 100%)',
  },
  'gradient-sunset': {
    type: 'gradient',
    value: 'linear-gradient(135deg, #f97316 0%, #fb923c 50%, #fdba74 100%)',
  },
  'gradient-purple': {
    type: 'gradient',
    value: 'linear-gradient(135deg, #7c3aed 0%, #8b5cf6 50%, #a78bfa 100%)',
  },
  'gradient-emerald': {
    type: 'gradient',
    value: 'linear-gradient(135deg, #059669 0%, #10b981 50%, #34d399 100%)',
  },
  'gradient-rose': {
    type: 'gradient',
    value: 'linear-gradient(135deg, #e11d48 0%, #f43f5e 50%, #fb7185 100%)',
  },
  'gradient-slate': {
    type: 'gradient',
    value: 'linear-gradient(135deg, #475569 0%, #64748b 50%, #94a3b8 100%)',
  },
  'gradient-amber': {
    type: 'gradient',
    value: 'linear-gradient(135deg, #d97706 0%, #f59e0b 50%, #fbbf24 100%)',
  },
  'pattern-dots': {
    type: 'pattern',
    value: 'radial-gradient(circle, #14b8a6 1px, transparent 1px)',
    backgroundSize: '20px 20px',
  },
  'pattern-grid': {
    type: 'pattern',
    value: 'linear-gradient(#e5e7eb 1px, transparent 1px), linear-gradient(90deg, #e5e7eb 1px, transparent 1px)',
    backgroundSize: '20px 20px',
  },
  'solid-white': {
    type: 'solid',
    value: '#ffffff',
  },
  'solid-gray': {
    type: 'solid',
    value: '#f3f4f6',
  },
  'solid-teal-light': {
    type: 'solid',
    value: '#ccfbf1',
  },
};

// Style par défaut du dashboard
const DEFAULT_BACKGROUND_STYLE: React.CSSProperties = {
  background: 'linear-gradient(135deg, #f0fdfa 0%, #ffffff 50%, #f0fdf4 100%)',
};

export interface UserBackgroundResult {
  /** Style CSS à appliquer au conteneur */
  backgroundStyle: React.CSSProperties;
  /** Type de background actuel */
  backgroundType: 'PREDEFINED' | 'CUSTOM' | 'NONE';
  /** ID ou URL du background */
  background: string | null;
  /** Indique si un background personnalisé est actif */
  hasCustomBackground: boolean;
}

export const useUserBackground = (): UserBackgroundResult => {
  const user = useAuthStore((state) => state.user);

  return useMemo(() => {
    const backgroundType = user?.backgroundType || 'NONE';
    const background = user?.background || null;

    // Pas de background personnalisé
    if (backgroundType === 'NONE' || !background) {
      return {
        backgroundStyle: DEFAULT_BACKGROUND_STYLE,
        backgroundType: 'NONE',
        background: null,
        hasCustomBackground: false,
      };
    }

    // Background prédéfini
    if (backgroundType === 'PREDEFINED') {
      const predefined = PREDEFINED_BACKGROUNDS[background];
      if (predefined) {
        const style: React.CSSProperties = predefined.type === 'solid'
          ? { backgroundColor: predefined.value }
          : {
              background: predefined.value,
              backgroundSize: predefined.backgroundSize || 'cover',
            };

        return {
          backgroundStyle: style,
          backgroundType: 'PREDEFINED',
          background,
          hasCustomBackground: true,
        };
      }
      // Background prédéfini non trouvé, utiliser le défaut
      return {
        backgroundStyle: DEFAULT_BACKGROUND_STYLE,
        backgroundType: 'NONE',
        background: null,
        hasCustomBackground: false,
      };
    }

    // Background personnalisé (image uploadée)
    if (backgroundType === 'CUSTOM') {
      return {
        backgroundStyle: {
          backgroundImage: `url(${background})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        },
        backgroundType: 'CUSTOM',
        background,
        hasCustomBackground: true,
      };
    }

    return {
      backgroundStyle: DEFAULT_BACKGROUND_STYLE,
      backgroundType: 'NONE',
      background: null,
      hasCustomBackground: false,
    };
  }, [user?.backgroundType, user?.background]);
};

export default useUserBackground;
